'use client'

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form'

import { updateUser } from '@/app/actions/user'
import { Checkbox } from '@/blocks/Form/Checkbox'
import { DayPicker } from '@/blocks/Form/DayPicker'
import { Error as FormError } from '@/blocks/Form/Error'
import { State } from '@/blocks/Form/State'
import { Text } from '@/blocks/Form/Text'
import { Textarea } from '@/blocks/Form/Textarea'
import { Drawer } from '@/components/Drawer'
import { FormAccessRequired } from '@/components/FormAccessRequired'
import { FormMessage } from '@/components/FormMessage'
import { Button } from '@/components/ui/button'
import { useTopics } from '@/hooks/useTopics'
import { useWinsContext } from '@/providers/WinsProvider'
import { formValidation } from '@/utilities/formValidation'
import { getClientSideURL } from '@/utilities/getURL'

import type { User } from '@/payload-types'

const WinsForm = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
  const { topics, loading: loadingTopics, error: errorTopics } = useTopics('wins')
  const { payloadUser } = useWinsContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const methods = useForm<FieldValues>({
    defaultValues: {
      firstName: payloadUser?.firstName ?? '',
      lastName: payloadUser?.lastName ?? '',
      email: payloadUser?.email ?? '',
      phone: payloadUser?.profile?.mobile ?? '',
    },
    mode: 'onChange',
  })
  // Subscribe to errors so the component re-renders when setError('topicSelection') is called
  const { errors } = methods.formState

  // Find the state category ID and watch its checkbox
  const stateTopicId = topics.find((topic) => topic.title?.toLowerCase() === 'state')?.id
  const watchStateTopic = useWatch({
    control: methods.control,
    name: stateTopicId ? `topic_${stateTopicId}` : '',
  })
  const stateActive = watchStateTopic === true

  // Watch topic checkboxes so we can clear topicSelection error when user selects at least one
  const topicFieldNames = topics.map((t) => `topic_${t.id}`)
  const watchedTopicValues = useWatch({ control: methods.control, name: topicFieldNames })
  const atLeastOneTopicSelected = Array.isArray(watchedTopicValues)
    ? watchedTopicValues.some(Boolean)
    : false
  useEffect(() => {
    if (atLeastOneTopicSelected) methods.clearErrors('topicSelection')
  }, [atLeastOneTopicSelected, methods])

  const onSubmit = async (data: FieldValues) => {
    if (!payloadUser) return

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    // Require at least one topic
    let selectedTopicIds = topics
      .filter((topic) => data[`topic_${topic.id}`] === true)
      .map((topic) => topic.id)
    if (selectedTopicIds.length === 0) {
      methods.setError('topicSelection', {
        type: 'manual',
        message: 'Please select at least one topic',
      })
      return
    }
    methods.clearErrors('topicSelection')

    const hasUserProfileFields =
      payloadUser?.id && ('firstName' in data || 'lastName' in data || 'email' in data)

    if (hasUserProfileFields && payloadUser) {
      const dataForSubmit: User = {
        ...payloadUser,
        firstName: data.firstName ?? payloadUser.firstName ?? '',
        lastName: data.lastName ?? payloadUser.lastName ?? '',
        profile: {
          ...payloadUser.profile,
          mobile: data.phone ?? payloadUser.profile?.mobile ?? '',
        },
      }

      try {
        const res = await updateUser({
          ...dataForSubmit,
        })

        if (res.status === 'error') {
          throw new Error(res.message)
        }
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Failed to update profile')
        setIsSubmitting(false)
        return
      }
    }

    try {
      const form = document.getElementById('wins-form') as HTMLFormElement
      const formData = new FormData(form)

      // FormData from form element will include all inputs including files
      // But we need to ensure all text fields are included (in case they're not in native inputs)
      // Since we're using react-hook-form, we'll append the data to ensure it's there
      formData.set('title', data.title ?? '')
      formData.set('winLink', data.winLink ?? '')
      formData.set('shortDescription', data.shortDescription ?? '')
      formData.set('winsDescription', data.winsDescription ?? '')

      formData.set('topics', selectedTopicIds.join(','))

      formData.set('firstName', data.firstName ?? '')
      formData.set('lastName', data.lastName ?? '')
      formData.set('email', data.email ?? '')
      formData.set('phone', data.phone ?? '')
      formData.set('state', data.state ?? '')
      formData.set('winDate', new Date(data.winDate).toISOString() ?? '')

      // if state is selected, we need to set the winScope to 'state'
      if (watchStateTopic === true) {
        formData.set('winScope', 'state')
      } else {
        formData.set('winScope', 'national')
      }

      const response = await fetch(`${getClientSideURL()}/api/frontend/wins/submit`, {
        method: 'POST',
        body: formData,
      })

      const result = (await response.json()) as { error?: string }

      if (!response.ok) {
        throw new Error(result.error ?? 'Failed to submit win')
      }

      setSubmitSuccess(true)
      // Reset to cleared values; include all field names so every field clears (defaultValues only had 4)
      const resetValues: FieldValues = {
        title: '',
        winLink: '',
        shortDescription: '',
        winsDescription: '',
        winDate: undefined,
        state: '',
        termsAndConditions: true,
      }
      selectedTopicIds = []
      topics.forEach((topic) => {
        resetValues[`topic_${topic.id}`] = false
      })
      methods.reset(resetValues)
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred while submitting the form'
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} setOpen={setOpen} ariaTitle="Submit a Win">
      {payloadUser ? (
        <FormProvider {...methods}>
          <form
            id="wins-form"
            onSubmit={methods.handleSubmit(onSubmit)}
            className="relative flex h-full flex-col"
          >
            <div className="relative flex h-full flex-col border-t border-border pt-6 font-family-body">
              <div className="flex flex-1 flex-col items-start justify-between pb-8 md:flex-row">
                <h3 className="pb-8 font-family-body text-2xl text-neutral-500 md:pb-0 md:pl-6">
                  MAHA Wins
                </h3>
                <div className="md:w-1/2 md:px-8">
                  <h2 className="font-family-body text-2xl font-semibold text-foreground">
                    Submit a win
                  </h2>
                  <p className="mt-3 text-card-foreground">
                    Know of a MAHA win we should feature? Submit it and we&apos;ll review the
                    details. Thanks for helping us celebrate progress together!
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-1 flex-col justify-end border-t border-border pt-6 md:flex-row">
                <div className="flex flex-col justify-between self-start md:w-1/2">
                  <div className="md:pl-6">
                    <h3 className="pb-8 font-family-body text-lg font-semibold text-foreground">
                      Win Information
                    </h3>
                    <div>
                      <Text
                        name="title"
                        label="Title"
                        blockType="text"
                        placeholder="Enter a name"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required
                        validation={{
                          ...formValidation.organizationName,
                        }}
                      />
                      <div className="h-8" />
                      <Text
                        name="winLink"
                        label="Website URL"
                        blockType="text"
                        placeholder="Enter a URL"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required
                        validation={formValidation.websiteUrl}
                      />
                      <div className="h-8" />
                      <Textarea
                        name="shortDescription"
                        label="Short Description"
                        blockType="text"
                        errors={methods.formState.errors}
                        register={methods.register}
                        placeholder="Enter a short description"
                        description="Enter a short description. Recommended length of approximately 100 - 200 characters. If not provided, the first few sentences will be used of the description."
                        {...({ validation: formValidation.shortDescription } as FieldValues)}
                      />
                      <div className="h-8" />
                    </div>
                  </div>
                </div>
                <div className="mb-8 flex flex-col justify-between self-start md:w-1/2">
                  <div className="md:px-8">
                    <Textarea
                      name="winsDescription"
                      label="Wins Description"
                      blockType="text"
                      errors={methods.formState.errors}
                      register={methods.register}
                      placeholder="Enter a description"
                      description="Enter a description. Recommended length of approximately 400 - 700 characters."
                      required
                      rows={10}
                      {...({ validation: formValidation.organizationDescription } as FieldValues)}
                    />
                    <div className="h-8" />
                  </div>
                  <div className="md:px-8">
                    <DayPicker
                      name="winDate"
                      control={methods.control}
                      errors={methods.formState.errors}
                      label="Date"
                      required
                      width="full"
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </div>
              </div>
              <div className="flex w-full flex-1 flex-col border-t border-border pt-6 md:flex-row">
                <div className="flex flex-col justify-between self-start">
                  <div className="pb-8 md:pl-6">
                    <h3 className="font-family-body text-lg font-semibold text-foreground">
                      Win Topics
                    </h3>
                    <p className="text-card-foreground">
                      Select the topics that best describe the win.(Select all that apply)*
                    </p>
                  </div>
                  <div className="grid w-full grid-cols-2 gap-4 overflow-hidden md:grid-cols-3 md:px-8 lg:grid-cols-4">
                    <>
                      {loadingTopics ? (
                        <div className="flex h-full items-center justify-center">
                          <Loader2 className="size-4 animate-spin" />
                        </div>
                      ) : errorTopics ? (
                        <div className="flex h-full items-center justify-center">
                          <p>Error loading topics</p>
                        </div>
                      ) : (
                        topics.map((topic) => (
                          <Checkbox
                            key={topic.id}
                            name={`topic_${topic.id}`}
                            label={topic.title}
                            register={methods.register}
                            errors={methods.formState.errors}
                            blockType="checkbox"
                          />
                        ))
                      )}
                      {errors.topicSelection && (
                        <div className="col-span-2 md:col-span-4">
                          <FormError name="topicSelection" />
                        </div>
                      )}
                    </>
                  </div>
                  <div className="h-8" />
                </div>
              </div>
              {stateActive && (
                <div className="flex-1 border-t border-border pt-6">
                  <div className="flex flex-col justify-between self-start">
                    <div className="pb-8 md:pl-6">
                      <h3 className="font-family-body text-lg font-semibold text-foreground">
                        Win State
                      </h3>
                      <p className="text-card-foreground">Select the state that the win is from.</p>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-4 md:grid-cols-4 md:px-6">
                      <State
                        name="state"
                        control={methods.control}
                        errors={methods.formState.errors}
                        label="State"
                        required
                        blockType="state"
                      />
                    </div>
                  </div>
                  <div className="h-8" />
                </div>
              )}
              <div className="flex-1 border-t border-border pt-6">
                <div className="flex h-full flex-col justify-between md:flex-row md:pl-6">
                  <div className="md:w-1/2">
                    <h3 className="pb-3 font-family-body text-lg font-semibold text-foreground">
                      Your Information
                    </h3>
                    <p className="text-card-foreground">
                      Your information is needed so we can contact you in regards of the submission
                      and update you on the review process.
                      <span className="mt-2 block text-xs text-muted-foreground">
                        * Any changes to your information will update your MAHA user details.
                      </span>
                    </p>
                  </div>
                  <div className="mt-8 md:mt-auto md:w-1/2 md:px-8">
                    <div className="mb-8 w-full">
                      <Text
                        name="firstName"
                        label="First Name"
                        blockType="text"
                        placeholder="Enter first name"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required
                        defaultValue={methods.getValues('firstName')}
                        validation={formValidation.firstName}
                      />
                      <div className="h-8" />
                      <Text
                        name="lastName"
                        label="Last Name"
                        blockType="text"
                        placeholder="Enter last name"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required
                        defaultValue={methods.getValues('lastName')}
                        validation={formValidation.lastName}
                      />
                      <div className="h-8" />
                      <Text
                        name="email"
                        label="Email"
                        blockType="text"
                        type="email"
                        placeholder="Enter an email address"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required
                        defaultValue={methods.getValues('email')}
                        validation={formValidation.email}
                        disabled={methods.getValues('email') ? true : false}
                      />
                      <div className="h-8" />
                      <Text
                        name="phone"
                        label="Phone"
                        blockType="text"
                        type="tel"
                        placeholder="Enter a phone number"
                        errors={methods.formState.errors}
                        register={methods.register}
                        required={false}
                        defaultValue={methods.getValues('phone')}
                        validation={formValidation.phone}
                      />
                      <div className="h-8" />
                      <Checkbox
                        name="termsAndConditions"
                        label="By checking this box, you consent to receive official MAHA® communications and agree to our [Privacy Policy](/privacy-policy) and [Terms of Service](/terms-of-service). Your information will be stored securely and used solely for authorized MAHA® purposes."
                        defaultValue={true}
                        errors={methods.formState.errors}
                        blockType="checkbox"
                        register={methods.register}
                        required
                      />
                      <Button type="submit" className="mt-3 w-full" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </Button>
                      {submitSuccess ? (
                        <FormMessage
                          type="success"
                          message="Win submitted successfully! It will be reviewed before being published."
                          className="mt-4"
                        />
                      ) : submitError ? (
                        <FormMessage
                          type="error"
                          message={submitError ?? 'An error occurred while submitting the win.'}
                          className="mt-4"
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      ) : (
        <div>
          <div className="flex flex-1 flex-col items-start justify-between pb-8 md:flex-row">
            <h3 className="pb-8 font-family-body text-2xl text-neutral-500 md:pb-0 md:pl-6">
              MAHA Wins
            </h3>
            <div className="md:w-1/2 md:px-8">
              <h2 className="font-family-body text-2xl font-semibold text-foreground">
                Submit a win
              </h2>
              <p className="mt-3 font-body text-card-foreground">
                Know of a MAHA win we should feature? Submit it and we&apos;ll review the details.
                Thanks for helping us celebrate progress together!
              </p>
            </div>
          </div>
          <div className="border-t border-border pt-6">
            <FormAccessRequired />
          </div>
        </div>
      )}
    </Drawer>
  )
}

export default WinsForm
